import { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { useT } from "../../../lib/i18n";

/**
 * Wheel geometry, from the v3 onboarding design and now used everywhere this
 * picker appears.
 *
 * Three rows rather than five, and rest rows are the SAME size as the
 * selected one — separated by weight and opacity instead. The old treatment
 * grew the type 14px → 18px as each value passed through the centre, which
 * made a calm control look busy. There's no backing gradient either: it read
 * as a slab sitting behind the wheel rather than as depth.
 */
const ITEM_H = 44;
const VISIBLE = 3;
/** Rows above and below the centre slot, so the first/last value can centre. */
const PAD_ROWS = Math.floor(VISIBLE / 2);
const ROW_PX = 16;
/** Quiet period after the last scroll tick before we call the value settled. */
const SETTLE_MS = 140;
/** Rows either side of centre that get a computed fade; beyond this is floor. */
const FADE_ROWS = 4;
const FAR_OPACITY = 0.04;

interface WheelColumnProps<T extends string | number> {
  values: T[];
  value: T;
  onChange: (v: T) => void;
  format?: (v: T) => string;
  ariaLabel: string;
  flex?: string;
}

/**
 * iOS-style scroll-snap wheel column. The center row is the "selected" slot;
 * the top + bottom each have two empty padding rows so the first/last value
 * can sit centered. We listen to scroll events with a debounce, then snap to
 * the nearest item and emit `onChange`.
 */
function WheelColumn<T extends string | number>({
  values,
  value,
  onChange,
  format = (v) => String(v),
  ariaLabel,
  flex = "flex-1",
}: WheelColumnProps<T>) {
  const ref = useRef<HTMLDivElement>(null);
  const settleRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track whether a programmatic scroll is in flight so we don't bounce
  // ourselves into a re-emit loop.
  const programmaticRef = useRef(false);
  const programmaticTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  // True from pointer/touch down until release. While this is set the column
  // belongs to the user and nothing is allowed to move it programmatically.
  const interactingRef = useRef(false);

  const valuesRef = useRef(values);
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  valuesRef.current = values;
  valueRef.current = value;
  onChangeRef.current = onChange;

  // Row nodes, so the distance fade can be written straight to the DOM. The
  // fade used to live in React state updated on every scroll tick, which
  // reconciled all ~80 rows at 60fps in the middle of an iOS momentum scroll.
  const rowsRef = useRef<(HTMLDivElement | null)[]>([]);
  rowsRef.current.length = values.length;
  // Indices touched by the last paint, so rows leaving the fade window get
  // reset exactly once instead of us writing to all 80 every frame.
  const paintedRef = useRef<number[]>([]);
  const rafRef = useRef<number | null>(null);

  // Convert a row's distance from the visual center (in row units) to a 0–1
  // opacity. Aggressive fade so the selection row is unmistakable: rows 1, 2,
  // 3 away land at ~0.4, 0.15, 0.05. Linear segments keep the falloff
  // predictable as the wheel scrolls between rows.
  const opacityForDistance = (d: number) => {
    if (d <= 1) return 1 - d * 0.6; // 1.0 → 0.4
    if (d <= 2) return 0.4 - (d - 1) * 0.25; // 0.4 → 0.15
    if (d <= 3) return 0.15 - (d - 2) * 0.1; // 0.15 → 0.05
    return FAR_OPACITY;
  };

  const paint = () => {
    const el = ref.current;
    if (!el) return;
    const rows = rowsRef.current;
    const center = el.scrollTop / ITEM_H;
    const nearest = Math.max(0, Math.min(rows.length - 1, Math.round(center)));
    const start = Math.max(0, Math.floor(center) - FADE_ROWS);
    const end = Math.min(rows.length - 1, Math.ceil(center) + FADE_ROWS);

    for (const i of paintedRef.current) {
      if (i >= start && i <= end) continue;
      const node = rows[i];
      if (!node) continue;
      node.style.opacity = String(FAR_OPACITY);
      node.style.fontWeight = "400";
    }

    const painted: number[] = [];
    for (let i = start; i <= end; i++) {
      const node = rows[i];
      if (!node) continue;
      node.style.opacity = String(opacityForDistance(Math.abs(i - center)));
      // Weight follows the scroll rather than the emitted value, so the
      // centred row reads as selected immediately instead of after settle.
      node.style.fontWeight = i === nearest ? "600" : "400";
      painted.push(i);
    }
    paintedRef.current = painted;
  };

  const schedulePaint = () => {
    if (rafRef.current != null) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      paint();
    });
  };

  // Align the scroll position to `value` when it changes from the outside
  // (the day column being clamped by a shorter month, a reset from the
  // parent, the desktop wheel handler stepping).
  //
  // This deliberately refuses to run while the user is touching the column or
  // while a scroll is still settling. Scrolling a live scroller out from under
  // a finger is what made this feel broken on iOS: the debounced emit below
  // fires while the finger is still down, the parent re-renders with a new
  // `value`, and the resulting smooth scrollTo took the gesture away — so each
  // touch could only ever move one row.
  const syncToValue = (behavior: ScrollBehavior) => {
    const el = ref.current;
    if (!el) return;
    if (interactingRef.current || settleRef.current) return;
    const i = values.indexOf(value);
    if (i < 0) return;
    if (Math.round(el.scrollTop / ITEM_H) === i) return;
    programmaticRef.current = true;
    el.scrollTo({ top: i * ITEM_H, behavior });
    if (programmaticTimerRef.current) clearTimeout(programmaticTimerRef.current);
    programmaticTimerRef.current = setTimeout(() => {
      programmaticRef.current = false;
    }, 250);
  };

  // Mount: jump straight to the initial value (no smooth scroll from row 0)
  // and lay down the first fade before the browser paints.
  const mountedRef = useRef(false);
  useLayoutEffect(() => {
    if (mountedRef.current) return;
    mountedRef.current = true;
    const el = ref.current;
    const i = values.indexOf(value);
    if (el && i >= 0) el.scrollTop = i * ITEM_H;
    paint();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    syncToValue("smooth");
    // `values` can change identity (day count per month) without the position
    // needing to move; syncToValue no-ops in that case.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, values]);

  // Repaint when the row set changes (e.g. Feb drops days 29–31).
  useEffect(() => {
    paintedRef.current = [];
    paint();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  useEffect(
    () => () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      if (settleRef.current) clearTimeout(settleRef.current);
      if (programmaticTimerRef.current)
        clearTimeout(programmaticTimerRef.current);
    },
    [],
  );

  // Touch ownership. Between touchstart and touchend the column is the user's;
  // iOS momentum then keeps firing scroll events, so the settle timer below
  // still resolves on its own once the fling comes to rest.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onTouchStart = () => {
      interactingRef.current = true;
      programmaticRef.current = false;
    };
    const onTouchEnd = () => {
      interactingRef.current = false;
    };
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    el.addEventListener("touchcancel", onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
    };
  }, []);

  // Desktop wheel handling — native CSS scroll-snap is unreliable for wheel
  // events (mouse wheel sends big jumps, trackpad sends fast small deltas;
  // `scroll-snap-stop: always` is honored inconsistently across browsers).
  // We intercept and step exactly one row per accumulated threshold of delta,
  // leaving touch interactions on the native scroll-snap path. Attached via
  // addEventListener with `passive: false` so we can preventDefault.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let accum = 0;
    let lastTime = 0;
    const STEP_DELTA = 24; // wheel delta accumulated before stepping one row
    const onWheel = (e: WheelEvent) => {
      if (e.ctrlKey) return; // let pinch-zoom through
      e.preventDefault();
      const now = performance.now();
      // Reset accumulator if there's a gap — separates discrete wheel
      // gestures (a click of the mouse wheel, a fresh trackpad swipe).
      if (now - lastTime > 200) accum = 0;
      lastTime = now;
      accum += e.deltaY;
      while (Math.abs(accum) >= STEP_DELTA) {
        const dir = accum > 0 ? 1 : -1;
        accum -= dir * STEP_DELTA;
        const arr = valuesRef.current;
        const idx = arr.indexOf(valueRef.current);
        const next = Math.max(0, Math.min(arr.length - 1, idx + dir));
        if (next === idx) continue;
        // A wheel tick is a fresh, deliberate input, so it outranks any
        // in-flight settle. Without dropping the pending timer, syncToValue
        // would refuse to move (it won't fight a settling scroll) and the
        // settle would then emit the old scroll position, eating the step.
        if (settleRef.current) {
          clearTimeout(settleRef.current);
          settleRef.current = null;
        }
        onChangeRef.current(arr[next]);
      }
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  // Mouse drag — click and drag vertically to scrub the wheel. Touch is left
  // to the native scroll path so momentum + scroll-snap still feel right on
  // mobile. We only handle `pointerType === "mouse"` here.
  //
  // Movement above DRAG_THRESHOLD pixels promotes the press into a drag and
  // captures the pointer; below the threshold, the press passes through as a
  // normal click on whichever row was pressed. After release, we snap to the
  // nearest row and swallow the synthetic `click` event so a drag-end doesn't
  // also pick a value.
  const dragRef = useRef({
    startY: 0,
    startScroll: 0,
    pointerId: -1,
    isDragging: false,
    swallowClick: false,
  });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const DRAG_THRESHOLD = 4;

    const onPointerDown = (e: PointerEvent) => {
      if (e.pointerType !== "mouse") return;
      if (e.button !== 0) return; // primary only
      interactingRef.current = true;
      dragRef.current = {
        startY: e.clientY,
        startScroll: el.scrollTop,
        pointerId: e.pointerId,
        isDragging: false,
        swallowClick: false,
      };
    };

    const onPointerMove = (e: PointerEvent) => {
      const d = dragRef.current;
      if (d.pointerId !== e.pointerId) return;
      const dy = e.clientY - d.startY;
      if (!d.isDragging && Math.abs(dy) > DRAG_THRESHOLD) {
        d.isDragging = true;
        // Capture so we keep getting events even if the cursor leaves the el.
        try {
          el.setPointerCapture(e.pointerId);
        } catch {
          // ignore: browser may refuse capture in some edge cases
        }
        el.style.cursor = "grabbing";
        // Disable CSS scroll-snap while dragging — otherwise the browser
        // treats each scrollTop set as a scroll-end and snaps the position
        // back to the nearest row, making the drag feel like it jumps
        // instead of scrubbing smoothly.
        el.style.scrollSnapType = "none";
      }
      if (d.isDragging) {
        e.preventDefault(); // suppress text selection
        el.scrollTop = d.startScroll - dy;
      }
    };

    const finishDrag = (e: PointerEvent) => {
      const d = dragRef.current;
      if (d.pointerId !== e.pointerId) return;
      interactingRef.current = false;
      el.style.cursor = "";
      if (d.isDragging) {
        d.swallowClick = true;
        try {
          el.releasePointerCapture(e.pointerId);
        } catch {
          // ignore
        }
        // Snap & emit. The smooth scrollTo also lets handleScroll fire its
        // debounce, but since the value will already match, that becomes a no-op.
        const i = Math.round(el.scrollTop / ITEM_H);
        const clamped = Math.max(
          0,
          Math.min(valuesRef.current.length - 1, i),
        );
        // Re-enable scroll-snap so the smooth scroll lands cleanly on the row.
        el.style.scrollSnapType = "y mandatory";
        el.scrollTo({ top: clamped * ITEM_H, behavior: "smooth" });
        const next = valuesRef.current[clamped];
        if (next !== valueRef.current) onChangeRef.current(next);
      }
      dragRef.current.pointerId = -1;
      dragRef.current.isDragging = false;
    };

    el.addEventListener("pointerdown", onPointerDown);
    el.addEventListener("pointermove", onPointerMove);
    el.addEventListener("pointerup", finishDrag);
    el.addEventListener("pointercancel", finishDrag);
    return () => {
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("pointermove", onPointerMove);
      el.removeEventListener("pointerup", finishDrag);
      el.removeEventListener("pointercancel", finishDrag);
    };
  }, []);

  const handleScroll = () => {
    schedulePaint();
    if (programmaticRef.current) return;
    // CSS `scroll-snap-type: y mandatory` handles the snap on both touch and
    // wheel — we only watch for the scroll settling and emit the centered
    // value to the parent. iOS keeps firing scroll events all through a
    // momentum fling, so this resolves when the fling actually rests rather
    // than mid-gesture.
    if (settleRef.current) clearTimeout(settleRef.current);
    settleRef.current = setTimeout(() => {
      settleRef.current = null;
      const elx = ref.current;
      if (!elx) return;
      const i = Math.round(elx.scrollTop / ITEM_H);
      const arr = valuesRef.current;
      const clamped = Math.max(0, Math.min(arr.length - 1, i));
      const next = arr[clamped];
      if (next !== valueRef.current) onChangeRef.current(next);
    }, SETTLE_MS);
  };

  return (
    <div
      role="listbox"
      aria-label={ariaLabel}
      className={`relative ${flex}`}
      style={{ height: ITEM_H * VISIBLE }}
    >
      {/* Scrollable list */}
      <div
        ref={ref}
        onScroll={handleScroll}
        className="h-full overflow-y-auto no-scrollbar select-none"
        style={{
          scrollSnapType: "y mandatory",
          // Keep an overscrolled fling inside the wheel. Without this, running
          // off either end of the list hands the gesture to the page behind
          // the picker and the wheel stops responding mid-swipe.
          overscrollBehaviorY: "contain",
          touchAction: "pan-y",
          // grab cursor as an affordance — flips to grabbing in the drag handler
          cursor: "grab",
        }}
      >
        <div style={{ height: ITEM_H * PAD_ROWS }} />
        {values.map((v, i) => {
          const isSelected = v === value;
          return (
            <div
              key={i}
              ref={(node) => {
                rowsRef.current[i] = node;
              }}
              role="option"
              aria-selected={isSelected}
              onClick={() => {
                // Suppress the synthetic click that fires at the end of a
                // mouse drag — the drag handler already snapped to the
                // correct value.
                if (dragRef.current.swallowClick) {
                  dragRef.current.swallowClick = false;
                  return;
                }
                onChange(v);
              }}
              className="text-center font-Poppins text-neutral-900 dark:text-neutral-50"
              // `opacity` and `fontWeight` are owned by paint() and written
              // straight to the node, so they track the scroll at 60fps
              // without re-rendering ~80 rows per frame.
              style={{
                height: ITEM_H,
                lineHeight: `${ITEM_H}px`,
                fontSize: `${ROW_PX}px`,
                scrollSnapAlign: "center",
                // Note: no `scroll-snap-stop: always`. It made iOS momentum
                // halt at the very next row, so a fling could never travel
                // more than one value — 40 swipes to cross the year column.
                // The desktop wheel path preventDefaults and steps manually,
                // so it never relied on snap-stop either.
              }}
            >
              {format(v)}
            </div>
          );
        })}
        <div style={{ height: ITEM_H * PAD_ROWS }} />
      </div>
    </div>
  );
}

interface DateWheelPickerProps {
  /** ISO date (YYYY-MM-DD). Defaults to 30 years ago. */
  value?: string;
  onChange: (iso: string) => void;
  /** Min year (oldest birthday). Default: 80 years ago. */
  minYear?: number;
  /** Max year (youngest birthday). Default: 18 years ago. */
  maxYear?: number;
}

/** Short month names in the active language (Jan/Feb… vs Jan/Feb/Mär…). */
function monthLabels(locale: string): string[] {
  const fmt = new Intl.DateTimeFormat(locale, { month: "short" });
  // Day 15 avoids any timezone rollover shifting the month.
  return Array.from({ length: 12 }, (_, i) =>
    fmt.format(new Date(Date.UTC(2021, i, 15))),
  );
}

/**
 * Whether the locale writes the day before the month (de/es/fr: 31.12.2021,
 * en-US: 12/31/2021). Drives the wheel column order — a German user reading
 * a month-first wheel has to work out that the middle column isn't the day.
 */
function isDayFirst(locale: string): boolean {
  try {
    const parts = new Intl.DateTimeFormat(locale).formatToParts(
      new Date(Date.UTC(2021, 11, 31)),
    );
    const day = parts.findIndex((p) => p.type === "day");
    const month = parts.findIndex((p) => p.type === "month");
    return day >= 0 && month >= 0 && day < month;
  } catch {
    return false;
  }
}

function daysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

function parseIso(iso?: string): { y: number; m: number; d: number } | null {
  if (!iso) return null;
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  return { y: parseInt(m[1], 10), m: parseInt(m[2], 10), d: parseInt(m[3], 10) };
}

function toIso(y: number, m: number, d: number): string {
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

/**
 * Three-column DOB wheel picker (Month / Day / Year).
 * Emits ISO date strings via onChange.
 */
export default function DateWheelPicker({
  value,
  onChange,
  minYear,
  maxYear,
}: DateWheelPickerProps) {
  const { t, language } = useT();
  const today = useMemo(() => new Date(), []);
  const yMax = maxYear ?? today.getFullYear() - 18;
  const yMin = minYear ?? today.getFullYear() - 100;

  // Default to a sensible age (~30) when the parent hasn't supplied a value.
  const defaultIso = useMemo(
    () => toIso(today.getFullYear() - 30, 7, 1),
    [today],
  );

  const parsed = parseIso(value) ?? parseIso(defaultIso)!;

  const years = useMemo(() => {
    const out: number[] = [];
    for (let y = yMax; y >= yMin; y--) out.push(y);
    return out;
  }, [yMin, yMax]);

  const months = useMemo(() => Array.from({ length: 12 }, (_, i) => i + 1), []);

  const days = useMemo(() => {
    const max = daysInMonth(parsed.y, parsed.m);
    return Array.from({ length: max }, (_, i) => i + 1);
  }, [parsed.y, parsed.m]);

  // Initialize when no value is provided so the parent gets a sensible default.
  useEffect(() => {
    if (!value) onChange(defaultIso);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emit = (y: number, m: number, d: number) => {
    const clampedDay = Math.min(d, daysInMonth(y, m));
    onChange(toIso(y, m, clampedDay));
  };

  // Month names and column order follow the active language, so a German
  // user gets "Mär" in a day-first wheel rather than "Mar" in a US one.
  const labels = useMemo(() => monthLabels(language), [language]);
  const dayFirst = useMemo(() => isDayFirst(language), [language]);

  const monthColumn = (
    <WheelColumn
      key="month"
      values={months}
      value={parsed.m}
      onChange={(m) => emit(parsed.y, m, parsed.d)}
      format={(m) => labels[m - 1]}
      ariaLabel={t("quiz.stepper.ageIntro.wheelMonth")}
      flex="basis-[35%]"
    />
  );

  const dayColumn = (
    <WheelColumn
      key="day"
      values={days}
      value={parsed.d}
      onChange={(d) => emit(parsed.y, parsed.m, d)}
      ariaLabel={t("quiz.stepper.ageIntro.wheelDay")}
      flex="basis-[25%]"
    />
  );

  return (
    <div className="relative">
      {/* Selection band — one horizontal marker across all three columns
          rather than three separate boxes, as a filled surface the value sits
          in. Both the fill and the hairline are theme-aware: this picker also
          renders on the light cream of the profile editor and the legacy
          quiz, where a white-on-white band would simply vanish. */}
      <div
        className="absolute left-2.5 right-2.5 pointer-events-none rounded-xl border border-black/10 dark:border-white/10 bg-black/[0.04] dark:bg-white/[0.05]"
        style={{
          top: ITEM_H * PAD_ROWS,
          height: ITEM_H,
        }}
      />

      <div className="relative flex items-stretch justify-center gap-5 px-2">
        {dayFirst ? dayColumn : monthColumn}
        {dayFirst ? monthColumn : dayColumn}
        <WheelColumn
          values={years}
          value={parsed.y}
          onChange={(y) => emit(y, parsed.m, parsed.d)}
          ariaLabel={t("quiz.stepper.ageIntro.wheelYear")}
          flex="basis-[40%]"
        />
      </div>
    </div>
  );
}
